import { useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { ModalFooter, ModalBody, ModalHeader, Modal, Button } from 'reactstrap';
import {
  fetchCalibrationWeightageNDueDate,
  fetchPerformance,
  performanceHeaderSubmit,
} from '../../../../store/apis/performanceService';
import { getCompanyCode } from '../../../../config/axios.config';
import { validateSubmission } from '../../../Performance/common';

function NextConfirmModal({ isOpen, toggle, headerData, refreshTable }) {
  const companyCode = getCompanyCode();

  const { mutate: headerSubmit, isPending } = useMutation({
    mutationFn: performanceHeaderSubmit,
    onSuccess: () => {
      toast.success('Submitted successfully!', {
        className: 'toast-success',
      });
      toggle();
      refreshTable();
    },
    onError: error => {
      toast.error(error?.message || 'Failed to submit data', {
        className: 'toast-error',
      });
    },
  });

  const {
    data: performanceDetails,
    isLoading: isPerformancePending,
    refetch: refetchPerformance,
  } = useQuery({
    queryKey: ['performanceDetails', headerData?.EmpCd, headerData?.ReviewPrd], // Add specific dependencies
    queryFn: () =>
      fetchPerformance({
        type: 'DETAIL',
        employeeId: headerData?.EmpCd?.trim(),
        period: headerData?.ReviewPrd?.trim(),
      }),
    enabled: !!headerData?.EmpCd && !!headerData?.ReviewPrd && isOpen,
    refetchOnMount: false, // Refetch when component mounts
    refetchOnWindowFocus: false, // Prevent unwanted refetches
  });

  const {
    data: weightagePercentage,
    isLoading: weightagePercentageLoading,
    refetch: refetchWeightagePercentage,
  } = useQuery({
    queryKey: ['weightagePercentage', headerData?.ReviewPrd, companyCode],
    queryFn: () =>
      fetchCalibrationWeightageNDueDate({
        type: 'weightage',
        fiscalYear: headerData?.ReviewPrd?.trim(),
      }),
    enabled: !!headerData?.ReviewPrd && !!companyCode && isOpen,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  // Add effect to refetch when headerData changes
  useEffect(() => {
    if (isOpen && headerData?.EmpCd && headerData?.ReviewPrd) {
      refetchPerformance();
      refetchWeightagePercentage();
    }
  }, [
    isOpen,
    headerData?.EmpCd,
    headerData?.ReviewPrd,
    refetchPerformance,
    refetchWeightagePercentage,
  ]);

  const handleYes = () => {
    const { isValid, message } = validateSubmission(
      headerData,
      performanceDetails,
      weightagePercentage,
    );
    if (!isValid) {
      toast.error(message, {
        className: 'toast-error',
      });
      toggle();
      return;
    }

    let status = 'Define_Objectives';
    if (headerData.Define_Objectives === 1) {
      status = 'Define_Objectives';
    } else if (headerData.Confirm_Objectives === 1) {
      status = 'Confirm_Objectives';
    } else if (headerData.Self_Assessment === 1) {
      status = 'Self_Assessment';
    } else if (headerData.Manager_Review === 1) {
      status = 'Manager_Review';
    } else if (headerData.Calibration_Result === 1) {
      status = 'Calibration_Result';
    } else {
      status = 'Define_Objectives';
    }

    const payload = {
      employeeId: headerData?.EmpCd?.trim(),
      userId: '',
      status,
    };
    headerSubmit(payload);
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      modalClassName="custom-modal-size"
      centered
    >
      <ModalHeader toggle={toggle}>Confirmation</ModalHeader>
      <ModalBody>Are you sure you want to move to the next step ?</ModalBody>
      <ModalFooter className="modalFooter">
        <Button className="item custom-light-btn" onClick={toggle}>
          No
        </Button>
        <Button
          className="item custom-btn"
          onClick={handleYes}
          disabled={
            isPerformancePending || weightagePercentageLoading || isPending
          }
        >
          {isPending ? 'Wait..' : 'Yes'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default NextConfirmModal;
